import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";

interface VideoContextType {
  videoUrl: string | null;
  setVideoUrl: (url: string | null) => void;
  videoRef: React.RefObject<HTMLVideoElement>;
  setVideoElement: (element: HTMLVideoElement | null) => void;
  isInitialized: boolean;
  setIsInitialized: (value: boolean) => void;
  currentTime: number;
  seekTo: (time: number) => void;
}

const VideoContext = createContext<VideoContextType | undefined>(undefined);

export const VideoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const setVideoElement = useCallback((element: HTMLVideoElement | null) => {
    if (videoRef.current !== element) {
      videoRef.current = element;
    }
  }, []);

  const seekTo = useCallback((time: number) => {
    if (videoRef.current) {
      if (videoRef.current) {
        videoRef.current.currentTime = time;
      }
      videoRef.current.play();
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      videoUrl,
      setVideoUrl: (url: string | null) => {
        if (videoUrl) {
          URL.revokeObjectURL(videoUrl);
        }
        setVideoUrl(url);
      },
      videoRef,
      setVideoElement,
      isInitialized,
      setIsInitialized,
      currentTime,
      setCurrentTime,
      seekTo,
    }),
    [videoUrl, isInitialized, currentTime, setVideoElement, seekTo]
  );

  return (
    <VideoContext.Provider value={contextValue}>
      {children}
    </VideoContext.Provider>
  );
};

export const useVideo = () => {
  const context = useContext(VideoContext);
  if (!context) {
    throw new Error("useVideo must be used within a VideoProvider");
  }
  return context;
};
